import React from 'react';
import './LegalPage.css';

interface PrivacyPolicyProps {
  onNavigate: (view: 'home' | 'terms') => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onNavigate }) => {
  return (
    <div className="legal-container">
      <div className="legal-header">
        <div className="legal-nav-bar">
          <button className="legal-back-btn" onClick={() => onNavigate('home')}>
            ← Back to Game
          </button>
          <div className="legal-quick-switch">
            <span
              className="legal-switch-link"
              onClick={() => onNavigate('terms')}
              role="button"
              tabIndex={0}
            >
              Terms of Service →
            </span>
          </div>
        </div>
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-last-updated">Last Updated: 21 September 2026</p>
      </div>

      <div className="legal-content">
        <article className="terms-of-service">
          <section>
            <h2>1. Information We Collect</h2>
            <p>
              We collect only the minimum necessary information required to provide, operate, and secure the Service:
            </p>
            <ul>
              <li>
                <strong>Account &amp; Authentication Information:</strong> When you create an account or sign in via Discord,
                we receive account details made available by Discord (such as your username, display name, user ID, avatar,
                and the email address associated with your Discord account). Your email address is processed and retained via
                our authentication infrastructure strictly to manage your account identity and maintain session integrity.
              </li>
              <li>
                <strong>Gameplay &amp; Save Data:</strong> To save and synchronize your progress across sessions and
                devices, we store your in-game virtual inventory, collection progress, wallet balance, and game achievements.
              </li>
              <li>
                <strong>Technical &amp; Log Data:</strong> Like most online services, our servers automatically receive
                standard technical information (such as IP addresses, browser types, and access timestamps) strictly
                for security, abuse prevention, and traffic management.
              </li>
              <li>
                <strong>Local Storage:</strong> We use local browser storage strictly to maintain your active login session
                and game interface preferences. We do not use third-party advertising or tracking cookies.
              </li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use the information collected solely to:</p>
            <ul>
              <li>Authenticate your login sessions and manage account access.</li>
              <li>Provide, maintain, and synchronize your gameplay progress.</li>
              <li>Display public in-game leaderboards and player statistics.</li>
              <li>Protect the Game and its players against cheating, automated exploitation, and unauthorized access.</li>
            </ul>
            <p>
              We do not sell, rent, or trade your personal information to third parties or advertisers, nor do we use your
              email address for marketing communications.
            </p>
          </section>

          <section>
            <h2>3. Third-Party Service Providers</h2>
            <p>
              We work with third-party service providers (such as cloud hosting, database, authentication, and network security
              partners) who process data on our behalf to help us operate and maintain the Service. These providers process
              data in accordance with their respective privacy and security standards.
            </p>
          </section>

          <section>
            <h2>4. Public Leaderboards</h2>
            <p>
              When you play with an account, your public username and in-game statistics (such as coins collected or completed sets)
              may appear on public leaderboards within the Game.
            </p>
          </section>

          <section>
            <h2>5. Data Retention &amp; Your Rights</h2>
            <p>
              We retain your gameplay data for as long as your account remains active. Depending on applicable privacy laws
              (including EU GDPR and California CCPA), you have the right to request access to, export, or permanently delete
              your account data.
            </p>
            <p>
              To request account deletion or data export, or if you have any questions regarding this Privacy Policy, please
              contact us at <a href="mailto:contact@euro-hunting-simulator.com">contact@euro-hunting-simulator.com</a>.
            </p>
            <p>
              You can also revoke Discord login authorization at any time in your Discord client under{' '}
              <em>User Settings &rarr; Authorized Apps</em>.
            </p>
          </section>

          <section>
            <h2>6. Data Security &amp; Limitation of Risk</h2>
            <p>
              We implement reasonable technical and organizational safeguards through our hosting and database infrastructure
              to help mitigate unauthorized access, disclosure, or alteration of your data.
            </p>
            <p>
              However, no system, data transmission over the Internet, or electronic storage method can be guaranteed to be
              100% secure. While we take standard measures to protect account information, we cannot warrant or guarantee
              absolute security, and you acknowledge that you use the Service and provide your information at your own risk.
            </p>
          </section>

          <section>
            <h2>7. Children&rsquo;s Privacy</h2>
            <p>
              The Service is not intended for children under 13 years of age. We do not knowingly collect personal information
              from children under 13.
            </p>
          </section>

          <section>
            <h2>8. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated
              &ldquo;Last Updated&rdquo; date.
            </p>
          </section>

          <section>
            <h2>9. Contact Us</h2>
            <p>
              If you have any questions or concerns regarding this Privacy Policy, please contact us at:{' '}
              <a href="mailto:contact@euro-hunting-simulator.com">contact@euro-hunting-simulator.com</a>.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};